<template>
  <header-buyout-header />
  <main class="main buyout">
    <v-progress-circular v-if="loadingFromUrl" />
    <template v-else-if="!buyoutProcessAllowed || disableBuyoutForManufacturer">
      <v-row>
        <v-col>
          <v-alert
            class="vAlert--errorAlt mt-16"
          >
            <template #prepend>
              <v-icon icon="ct:circle-exclamation" />
            </template>
            <div>
              <span v-if="get(buyoutProcessErrorMessage, 'wrong_buyout_process')">
                {{ get(buyoutProcessErrorMessage, 'wrong_buyout_process') }}
              </span>
              <span v-else>
                {{ t("static.bonuses.new_device_process_not_available") }}
              </span>
            </div>
          </v-alert>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col cols="4" class="text-center">
          <v-btn
            class="mbtn mbtn--trans mbtn--multiline"
            size="x-large"
            :to="indexLink"
          >
            {{ t("static.bonuses.back_to_homepage") }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <component :is="appraisalComponent" v-if="mainBuyoutStep===0" />
      <buyout-bonus-selector v-else-if="mainBuyoutStep===1" />
      <component :is="personalDataComponent" v-else-if="mainBuyoutStep === 2" />
      <buyout-thank-you v-if="mainBuyoutStep===3" />
    </template>
  </main>
</template>

<script setup lang="ts">

import {storeToRefs} from "pinia";
import type {ConcreteComponent} from "vue";
import {useUrlSearchParams} from "@vueuse/core";
import {get} from "lodash-es";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/buyout/[[type]]/[[manufacturer]]/[[meta_master]]/[[variant]]/[[color]]/[[quality]]",
    // @ts-ignore - beacause of i18n generated locale type
    sk: "/vykup/[[type]]/[[manufacturer]]/[[meta_master]]/[[variant]]/[[color]]/[[quality]]",
    // @ts-ignore - beacause of i18n generated locale type
    cs: "/vykup/[[type]]/[[manufacturer]]/[[meta_master]]/[[variant]]/[[color]]/[[quality]]"
  }
});
const searchParams = useUrlSearchParams("history");
definePageMeta({auth: false});

const api = useApi();
const { $api } = useNuxtApp() as { $api: IApiInstance };
const {notify} = useNotification();
const localePath = useLocalePath();
const {t} = useI18n();
const {indexLink} = useHeader();

const buyoutStore = useBuyoutStore();
const authStore = useAuthStore();
const partnerStore = usePartnerStore();
const route = useRoute();

useSeoMeta({
  title: t("seo.buyout.title"),
  description: t("seo.buyout.description"),
});

const {
  appraisalBuyoutStep,
  mainBuyoutStep,
  personalDataStep,
  buyoutLoadedFromUrl,
  buyoutProcessAllowed,
  buyoutProcessErrorMessage
} = storeToRefs(buyoutStore);

const loadingFromUrl = ref<boolean>(false);

onMounted(() => {
  buyoutStore.resetBuyoutProcess();
  CheckRoute();
});

// @ts-ignore
const pageAccessedByReload = computed<boolean>(() => import.meta.client && ((window.performance.navigation && window.performance.navigation.type === 1) || window.performance.getEntriesByType("navigation").map(nav => nav.type).includes("reload")));

async function CheckRoute() {
  loadingFromUrl.value = true;

  if (searchParams.refId) {
    buyoutStore.buyoutReferralId = searchParams.refId as string;
    buyoutStore.buyoutReferralLinkId = searchParams.linkId as string || null;
    const {data, error} = await $api.user.partnerByAmbassadorRef(buyoutStore.buyoutReferralId as string);
    if (data.value) {
      partnerStore.partner = data.value;
      if (!pageAccessedByReload.value && (!authStore?.data?.reference_code || buyoutStore.buyoutReferralId !== authStore!.data!.reference_code)) {
        const body: any = {
          ref_id: buyoutStore.buyoutReferralId,
          link_id: buyoutStore.buyoutReferralLinkId
        };
        if (searchParams.refType) {
          body.ref_type = searchParams.refType as string;
        }
        await api.call<void>(api.commonUrls.log.REFERRAL_OPEN(), {
          method: "POST",
          body: {
            ...body
          }
        });
      }
      partnerStore.gtmLoad();
    }

    if (error.value) {
      if (error.value.statusCode === 400) {
        buyoutProcessAllowed.value = false;
        buyoutProcessErrorMessage.value = error.value.data;
      }
    }
  }

  if (import.meta.client) {
    await buyoutStore.loadAvailableDeviceTypes();
    if (route.params?.type) {
      const type = buyoutStore.availableDeviceTypes.find(t => t.seo_name === route.params.type);
      if (type !== undefined) {
        buyoutStore.selectedDeviceType = type;
        if (!route.params.manufacturer) {
          await buyoutStore.loadAvailableManufacturers();
          appraisalBuyoutStep.value = 1;
        }
      }
    }

    if (route.params.manufacturer) {
      if (!buyoutStore.availableManufacturers.length) {
        await buyoutStore.loadAvailableManufacturers();
      }
      const manufacturer = buyoutStore.availableManufacturers.find(t => t.seo_name === route.params.manufacturer);
      if (manufacturer !== undefined) {
        buyoutStore.selectedManufacturer = manufacturer;
        if (!route.params.meta_master) {
          await buyoutStore.loadAvailableMetaMasters();
          appraisalBuyoutStep.value = 2;
        }
      }
    }
    if (route.params.meta_master) {
      if (!buyoutStore.availableMetaMasters.length) {
        await buyoutStore.loadAvailableMetaMasters();
      }
      const metaMaster = buyoutStore.availableMetaMasters.find(t => t.seo_name === route.params.meta_master);
      if (metaMaster !== undefined) {
        buyoutStore.selectedMetaMaster = metaMaster;
        if (!route.params.variant) {
          await buyoutStore.loadAvailableProductVariants();
          appraisalBuyoutStep.value = 3;
        }
      }
    }

    if (route.params.variant) {
      if (!buyoutStore.availableProductVariants) {
        await buyoutStore.loadAvailableProductVariants();
      }

      if (
        buyoutStore.availableProductVariants?.attr_values?.length &&
        buyoutStore.availableProductVariants.attr_values.length > 0
      ) {
        const productVariant = buyoutStore.availableProductVariants.attr_values.find(
          (attr: BuyoutDeviceAttributeValue) =>
            attr.master_product.seo_name === route.params.variant ||
            attr.master_product.old_seo_name === route.params.variant
        );

        if (productVariant !== undefined) {
          buyoutStore.selectedProductVariant = productVariant;
          if (!route.params.color) {
            await buyoutStore.loadAvailableProductColors();
            appraisalBuyoutStep.value = 3;
          }
        }
      }
    }

    if (route.params.color) {
      if (!buyoutStore.availableProductColors.length) {
        await buyoutStore.loadBonusPeriod();
        await buyoutStore.loadAvailableProductColors();
      }

      const productColor = buyoutStore.availableProductColors.find(
        (t) => t?.seo_name === route.params.color || t?.old_seo_name === route.params.color
      );

      if (productColor !== undefined) {
        buyoutStore.selectedProductColor = productColor;
        if (!route.params.quality) {
          await buyoutStore.loadAvailableProductAppraisal();
          appraisalBuyoutStep.value = 4;
        }
      }
    }


    if (route.params.quality) {
      if (!buyoutStore.availableDeviceQualities.length) {
        await buyoutStore.loadAvailableProductAppraisal();
      }
      const productQuality = buyoutStore.availableDeviceQualities?.find(t => t.quality.short_name === route.params.quality);
      if (productQuality !== undefined) {
        buyoutStore.selectedProductQuality = productQuality;
        appraisalBuyoutStep.value = 4;
      }
    }
  }
  if (searchParams.userBuyoutCode) {
    const {
      data,
      error
    } = await api.call<EnhancedBuyoutDetail>(api.commonUrls.buyout.ENHANCED_BUYOUT_DETAIL_BY_NUMBER(searchParams.userBuyoutCode as string));
    if (error.value) {
      notify({
        title: t("form.common.error"),
        text: t("general.error_sending_request"),
        type: "error"
      });
    }
    if (data.value) {
      // Check buyout state and redirect if buyout was already saved
      if (data?.value?.status?.identifier && data.value.status.identifier !== "new") {
        await navigateTo(localePath({name: "buyout-detail-id", params: {id: data.value.registration_number}}));
        return;
      }
      // Set first step, when buyout is already created
      const tmpAppraisalForwardSteps = [4];
      const tmpMainBuyoutForwardSteps = [0];
      const tmpPersonalDataForwardSteps = [0];

      const tmpAppraisalErrorSteps: Array<number> = [];
      const tmpMainBuyoutErrorSteps: Array<number> = [];
      const tmpPersonalDataErrorSteps: Array<number> = [];

      buyoutStore.userBuyoutCode = data.value.registration_number;
      buyoutStore.buyoutDeviceImei = data.value.old_device_imei;
      partnerStore.partner = data.value.partner_data;
      await buyoutStore.loadAvailableBonusPartners();

      // Defects
      await buyoutStore.handleDefects(data.value.defects);

      // Check if defect answers are still valid, or if there is no new defect answer required
      if (!buyoutStore.defectsAnswers.every(da => da !== null)) {
        tmpMainBuyoutErrorSteps.push(0);
        tmpAppraisalErrorSteps.push(4);
      }

      // Bonuses
      if (data.value.new_device_additional_data && data.value.new_device_partner) {
        await buyoutStore.loadAvailableBonusPartners();
        buyoutStore.selectedBonusPartner = buyoutStore.availableBonusPartners.find(partner => partner.id === data.value.new_device_partner.id) || null;
        if (!buyoutStore.selectedBonusPartner) {
          buyoutStore.selectedBonusPartner = null;
          return;
        }
        await buyoutStore.loadAvailableNewMetaMasters();
        // We need to check if buyout's bonus is fallback bonus, because for fallback bonuses we load additional data
        const fallbackBonusPath = data.value.fallback_bonus !== "";
        buyoutStore.selectedNewMetaMaster = buyoutStore.availableNewMetaMasters.find(mm => mm.id === (fallbackBonusPath ? data.value.fallback_bonus : data.value.new_device_additional_data.new_meta_master_pk)) || null;
        if (buyoutStore.selectedNewMetaMaster) {
          if (fallbackBonusPath) {
            await buyoutStore.loadAvailableNewManufacturers();
            buyoutStore.selectedNewManufacturer = buyoutStore.availableNewManufacturers.find(sm => sm.id === data.value.new_device_additional_data.manufacturer_pk) || null;
            await buyoutStore.loadAvailableOthersMetaMasters();
            buyoutStore.selectedOthersNewMetaMasters = buyoutStore.availableOthersNewMetaMasters.find(om => om.id === data.value.new_device_additional_data.new_meta_master_pk) || null;
            await buyoutStore.loadAvailableNewMasters(true);
          } else {
            await buyoutStore.loadAvailableNewMasters();
          }

          if (Array.isArray(buyoutStore.availableNewProductVariants?.attr_values)) {
            buyoutStore.selectedNewProductVariant = buyoutStore.availableNewProductVariants?.attr_values.find(pv => pv.id === data.value.new_device_additional_data.attr_value_pk) || null;
          } else {
            buyoutStore.selectedNewProductVariant = null;
          }
          if (buyoutStore.selectedNewProductVariant) {
            await buyoutStore.loadAvailableNewColors();
            buyoutStore.selectedNewProductColor = buyoutStore.availableNewProductColors.find(pc => pc.id === data.value.new_device.id) || null;
            await buyoutStore.loadBuyoutBonuses();
          }
          if (!buyoutStore.selectedBuyoutBonuses || buyoutStore.selectedBuyoutBonuses.length === 0) {
            buyoutStore.selectedNewMetaMaster = null;
            buyoutStore.selectedNewProductVariant = null;
            buyoutStore.selectedNewProductColor = null;
            tmpMainBuyoutErrorSteps.push(1);
          }
          tmpMainBuyoutForwardSteps.push(2);
        } else {
          tmpMainBuyoutErrorSteps.push(1);
        }
      }

      // Customer data
      if (data.value.customer_info) {
        tmpAppraisalErrorSteps.push(4);
        buyoutStore.buyoutPersonalData = data.value.customer_info;
        buyoutLoadedFromUrl.value = true;
        tmpMainBuyoutForwardSteps.push(2);
        tmpPersonalDataForwardSteps.push(1);
      }

      if (data.value.status_identifier && data.value.status_identifier === "saved") {
        tmpMainBuyoutForwardSteps.push(3);
      }

      mainBuyoutStep.value = tmpMainBuyoutErrorSteps.length > 0 ? Math.min(...tmpMainBuyoutErrorSteps) : Math.max(...tmpMainBuyoutForwardSteps);
      appraisalBuyoutStep.value = tmpAppraisalErrorSteps.length > 0 ? Math.min(...tmpAppraisalErrorSteps) : Math.max(...tmpAppraisalForwardSteps);
      personalDataStep.value = tmpPersonalDataErrorSteps.length > 0 ? Math.min(...tmpPersonalDataErrorSteps) : Math.max(...tmpPersonalDataForwardSteps);
    }
    loadingFromUrl.value = false;
  }

  loadingFromUrl.value = false;
}

const appraisalComponent = computed<ConcreteComponent | string>(() => {
  switch (appraisalBuyoutStep.value) {
  case 1:
    return resolveComponent("BuyoutManufacturerSelector");
  case 2:
    return resolveComponent("BuyoutMetaMasterProductSelector");
  case 3:
    return resolveComponent("BuyoutProductVariantSelector");
  case 4:
    return resolveComponent("BuyoutQualitySelector");
  default:
    return resolveComponent("BuyoutDeviceTypeSelector");
  }
});
const personalDataComponent = computed<ConcreteComponent | string>(() => {
  switch (personalDataStep.value) {
  case 1:
    return resolveComponent("BuyoutDeliverySelector");
  default:
    return resolveComponent("BuyoutCustomerDataForm");
  }
});

const disableBuyoutForManufacturer = computed<boolean>(() => {
  const partner = partnerStore.getBuyoutPartner;
  return partner && partner?.use_new_device_process;

});

</script>
